import React from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapPin, ExternalLink } from 'lucide-react';

// Fix Leaflet default icon paths (required with Webpack builds)
if (typeof window !== 'undefined' && L?.Icon?.Default) {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });
}

/**
 * Mini carte de position du bien, réutilisable partout.
 * Props:
 *  - latitude, longitude (numbers required)
 *  - address, address_precision (optional labels affichés dessous)
 *  - height (default 220)
 *  - zoom (default 17)
 */
export default function ProjectMiniMap({ latitude, longitude, address, address_precision, height = 220, zoom = 17 }) {
  if (typeof latitude !== 'number' || typeof longitude !== 'number' || !isFinite(latitude) || !isFinite(longitude)) {
    return (
      <div
        className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-xs text-slate-500"
        style={{ height }}
        data-testid="project-minimap-missing"
      >
        <MapPin className="w-4 h-4 mr-2" /> Coordonnées GPS non disponibles
      </div>
    );
  }

  const osmLink = `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=${zoom}/${latitude}/${longitude}`;
  const gmapsLink = `https://www.google.com/maps?q=${latitude},${longitude}`;

  return (
    <div className="rounded-lg overflow-hidden border border-navy/10 shadow-sm" data-testid="project-minimap">
      <div style={{ height }}>
        <MapContainer
          center={[latitude, longitude]}
          zoom={zoom}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={false}
          dragging={true}
          doubleClickZoom={false}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={[latitude, longitude]} />
        </MapContainer>
      </div>
      <div className="bg-slate-50 px-3 py-2 flex items-center justify-between flex-wrap gap-2 text-xs">
        <div className="flex items-center gap-1.5 text-slate-700 min-w-0 flex-1">
          <MapPin className="w-3.5 h-3.5 text-navy flex-shrink-0" />
          <span className="truncate">
            {address || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`}
            {address_precision && <span className="italic text-amber-700"> — {address_precision}</span>}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a href={osmLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
            OSM <ExternalLink className="w-3 h-3" />
          </a>
          <a href={gmapsLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
            Google <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
